import React from 'react';
import { Alert, Button, InputNumber, Select, Skeleton, Space, Table, Tag, Tooltip, Typography } from 'antd';
import DateWithWeekday from '../../../components/DateWithWeekday';
import {
  REBALANCE_ACTION_ALLOT,
  REBALANCE_ACTION_RELEASE,
  REBALANCE_ACTIONS,
  REBALANCE_DAY_OPTIONS,
  REBALANCE_DEFAULT_MIN_UNSOLD,
  REBALANCE_MIN_UNSOLD,
} from '../InventoryPage/inventoryData';
import { REBALANCE_ACTION_ALL } from './listPreferences';
import { FONT } from "../../../styles/typography";

const { Text } = Typography;

const REBALANCE_FG = '#d46b08';

/**
 * 兩種動作要做的事相反 (一個把房從線上收回來給櫃台，一個把房推去線上)，
 * 顏色分開才能一眼掃出這一頁裡哪些是同一類、可以一起處理。
 *
 * 加配額給 OTA 沿用矩陣裡調控提醒的橘色；釋出給現場用藍色，與週末的紅字區隔。
 */
const ACTION_COLORS = {
  [REBALANCE_ACTION_ALLOT]: 'orange',
  [REBALANCE_ACTION_RELEASE]: 'blue',
};

/**
 * 待調控清單的工具列與表格。查詢與過濾都在頁面層做，這裡只負責呈現。
 *
 * 與庫存矩陣互補而非取代：矩陣適合看某段期間的全貌與前後幾天的趨勢，清單適合逐項處理。
 * 清單固定看未來的 N 天，跟矩陣的查詢區間無關 —— 要一次看完所有待處理項目。
 */
export default function RebalanceList({
  items = [],
  hiddenCount = 0,
  actionHiddenCount = 0,
  minUnsoldHiddenCount = 0,
  loading = false,
  range = null,
  error = null,
  days,
  onDaysChange,
  action = REBALANCE_ACTION_ALL,
  onActionChange,
  minUnsold = REBALANCE_DEFAULT_MIN_UNSOLD,
  onMinUnsoldChange,
  roomTypes = [],
  excluded = [],
  onExcludedChange,
  onRefresh,
}) {
  const rangeText = range
    ? `${range[0].format('YYYY/MM/DD')} ~ ${range[1].format('YYYY/MM/DD')}`
    : null;

  const daySelect = (
    <Select value={days} onChange={onDaysChange} style={{ width: 110 }} disabled={loading}>
      {REBALANCE_DAY_OPTIONS.map((d) => (
        <Select.Option key={d} value={d}>未來 {d} 天</Select.Option>
      ))}
    </Select>
  );

  // 建議篩選放在工具列而不是表頭的漏斗：表頭的篩選離開這一頁就沒了，
  // 但「我只負責加配額」是每天固定的分工，跟排除房型一樣值得記住
  const actionSelect = (
    <Select value={action} onChange={onActionChange} style={{ width: 150 }} disabled={loading}>
      <Select.Option value={REBALANCE_ACTION_ALL}>全部建議</Select.Option>
      {REBALANCE_ACTIONS.map((a) => (
        <Select.Option key={a} value={a}>{a}</Select.Option>
      ))}
    </Select>
  );

  // 未售數量門檻：差 2 間的失衡調不調影響有限，但天天都在清單上佔位置。
  // 下限是判定規則的底線 (2 間) —— 再往下調也不會多出任何一筆
  const minUnsoldInput = (
    <span>
      <Text type="secondary">未售 ≥</Text>
      <InputNumber
        data-testid="min-unsold"
        min={REBALANCE_MIN_UNSOLD}
        max={99}
        precision={0}
        value={minUnsold}
        onChange={onMinUnsoldChange}
        disabled={loading}
        style={{ width: 64, marginLeft: 8 }}
      />
    </span>
  );

  // 排除房型：長租、整修中或本來就不打算上線賣的房型會天天出現在清單上，
  // 對訂房組而言是固定雜訊，會蓋掉真正要處理的項目
  const roomTypeSelect = (
    <Select
      mode="multiple"
      allowClear
      value={excluded}
      onChange={onExcludedChange}
      placeholder="排除房型"
      style={{ width: 360 }}
      maxTagCount="responsive"
      // 標籤只放房型名稱：加上代碼會讓每個標籤長一倍，選兩個就擠不下、
      // 被收成「+2 ...」而看不出到底排除了什麼。代碼仍然搜尋得到。
      filterOption={(input, option) => {
        const keyword = input.trim().toLowerCase();
        return option.label.toLowerCase().includes(keyword)
          || option.value.toLowerCase().includes(keyword);
      }}
      options={roomTypes.map((type) => ({ value: type.cod, label: type.nam }))}
    />
  );

  const toolbar = (
    <Space style={{ marginBottom: 12 }} wrap>
      {daySelect}
      {actionSelect}
      {minUnsoldInput}
      {roomTypeSelect}
      {onRefresh && (
        <Button size="small" loading={loading} onClick={onRefresh}>重新整理</Button>
      )}
    </Space>
  );

  if (error) {
    return (
      <>
        {toolbar}
        <Alert
          type="error"
          showIcon
          message="無法取得待調控清單"
          description={error}
          action={onRefresh ? <Button size="small" onClick={onRefresh}>重試</Button> : null}
        />
      </>
    );
  }

  // loading 時不可渲染空表格 —— antd 的「暫無資料」會被讀成「沒有待調控項目」，
  // 那是相反的結論。理由同矩陣那邊對 payload 為 null 的處理。
  if (loading && items.length === 0) {
    return (
      <>
        {toolbar}
        <Skeleton active paragraph={{ rows: 5 }} />
      </>
    );
  }

  // 每一欄都給 sorter：同一份清單有好幾種處理順序 —— 照日曆逐日走、
  // 把同房型的湊在一起一次調、或先解決線上完全沒房可賣的那些，
  // 少了任何一欄的排序就得退回矩陣頁自己找
  const columns = [
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      width: 210,
      // 日期是 YYYY/MM/DD，字串比大小就等於時間先後，不必先轉成 moment
      sorter: (a, b) => a.date.localeCompare(b.date),
      // 預設由近到遠 —— 訂房組是照日曆逐日處理的，最近的日期最急、也最來得及調。
      // 同一天內沿用資料本身的順序 (未售多者在前，Array.sort 穩定)
      defaultSortOrder: 'ascend',
      // 改走共用元件才會標出特別假日與補班日 —— 原本只依星期幾上紅色，
      // 春節的週六跟普通週六分不出來，補班的週六還會被標成假日
      render: (date) => <DateWithWeekday date={date} />,
    },
    {
      title: '房型',
      key: 'room',
      width: 210,
      // 依名稱排，同名不同代碼 (例如拆成兩批的同型房) 再比代碼，才不會交錯在一起
      sorter: (a, b) => a.nam.localeCompare(b.nam, 'zh-Hant') || a.cod.localeCompare(b.cod),
      render: (_, row) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {row.nam}
          <Text type="secondary" style={{ fontSize: FONT.caption, marginLeft: 6 }}>
            {row.cod} · 共 {row.room_qnt} 間
          </Text>
        </span>
      ),
    },
    {
      title: '現場可售',
      dataIndex: 'left_qnt',
      key: 'left_qnt',
      align: 'right',
      width: 90,
      sorter: (a, b) => a.left_qnt - b.left_qnt,
      render: (v) => <span style={{ color: v <= 0 ? REBALANCE_FG : 'inherit' }}>{v}</span>,
    },
    {
      title: '線上銷售',
      dataIndex: 'wrs_left',
      key: 'wrs_left',
      align: 'right',
      width: 90,
      sorter: (a, b) => a.wrs_left - b.wrs_left,
      render: (v) => <span style={{ color: v <= 0 ? REBALANCE_FG : 'inherit' }}>{v}</span>,
    },
    {
      title: '未售',
      dataIndex: 'unsold',
      key: 'unsold',
      align: 'right',
      width: 80,
      // 要先處理影響最大的就點這一欄改排未售，預設仍以日期為準
      sorter: (a, b) => a.unsold - b.unsold,
      render: (v) => <b>{v}</b>,
    },
    {
      title: '建議',
      dataIndex: 'action',
      key: 'action',
      width: 150,
      // 依 REBALANCE_ACTIONS 的次序排，同一種動作聚成一段可以一次處理完。
      // 不用字串比較 —— 中文的排序結果跟畫面上的順序對不起來，看起來像隨機的
      sorter: (a, b) => REBALANCE_ACTIONS.indexOf(a.action) - REBALANCE_ACTIONS.indexOf(b.action),
      render: (value, row) => (
        <Tooltip title={row.message}>
          <Tag color={ACTION_COLORS[value]} style={{ cursor: 'help' }}>{value}</Tag>
        </Tooltip>
      ),
    },
  ];

  // 三個條件各自濾掉幾筆都要講 —— 否則筆數變少會被讀成情況好轉。
  // 集中成一份，表格有資料時當註腳、空清單時當原因，兩邊不會講得不一樣
  const filterNotes = [
    hiddenCount > 0 && `已排除 ${excluded.length} 個房型，隱藏 ${hiddenCount} 筆`,
    actionHiddenCount > 0 && `只顯示「${action}」，另有 ${actionHiddenCount} 筆是其他建議`,
    minUnsoldHiddenCount > 0 && `只看未售 ${minUnsold} 間以上，另有 ${minUnsoldHiddenCount} 筆未達門檻`,
  ].filter(Boolean);

  return (
    <>
      {toolbar}

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">
          {rangeText && `${rangeText}　`}
          該房型當天還有 <Text strong>{minUnsold} 間以上未售</Text>、
          但<Text strong>其中一邊通路已無庫存可賣</Text>的項目。
          滑鼠移到「建議」上有具體說明。
        </Text>
        {/* 清單空掉時不在這裡重複 —— 底下的提示會把原因說得更完整 */}
        {items.length > 0 && filterNotes.length > 0 && (
          <div>
            <Text type="warning">{filterNotes.join('；')}。</Text>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <Alert
          type={filterNotes.length > 0 ? 'info' : 'success'}
          showIcon
          message={
            filterNotes.length > 0
              ? '待調控項目都被目前的條件濾掉了 —— 放寬上方任一個條件才看得到'
              : '沒有需要調控的房型日 —— 各房型兩邊通路都還有庫存可賣'
          }
          description={filterNotes.length > 0 ? `${filterNotes.join('；')}。` : null}
        />
      ) : (
        <Table
          size="small"
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={
            items.length > 25
              ? {
                pageSize: 25,
                showSizeChanger: false,
                showTotal: (total, [start, end]) => `第 ${start}~${end} 筆，共 ${total} 筆`,
              }
              : false
          }
        />
      )}
    </>
  );
}
